// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { minicartStyles } from "@bg/App/Components/XngMiniCart/XngMiniCartStyles";
import Button from "@bg/MobiusOverrides/Button/Button";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import Link from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const customSettings = getSettingsCollection(state).customSettings as any;

    return {
        cart,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        isCartCheckoutDisabled: isCartCheckoutDisabled(cart),
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isAuth: getSession(state).isAuthenticated,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        signUpPageUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
        customSettings,
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

type OwnProps = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory & { closeOnClick: any };

const MiniCartActionButtons: React.FC<OwnProps> = ({
    cart,
    cartPageUrl,
    isPunchOutOrder,
    canSubmitRequisition,
    closeOnClick,
    isAuth,
    signInPageUrl,
    signUpPageUrl,
    customSettings,
    history,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
}) => {
    const [unAuthDisable, setUnauthDisabled] = React.useState(false);

    React.useEffect(() => {
        if (
            customSettings &&
            customSettings.postalCodeLocationFinder.toLowerCase() === "true" &&
            !window.localStorage.getItem("postalCodeID")?.length &&
            !isAuth
        ) {
            setUnauthDisabled(true);
        }
    }, []);

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            // to refresh the page and get the new cart lines properties
            window.location.href = rfqRequestQuotePageUrl;
        }
    };
    const submitForQuoteLabel = cart?.isSalesperson
        ? translate("Create a Quote")
        : translate("Request Quote for All Items");

    return (
        <StyledWrapper {...minicartStyles.minicartActionButtonsStyles}>
            {canSubmitForQuote && (
                <StyledWrapper {...minicartStyles.minicartQuoteButton}>
                    <Link
                        {...minicartStyles.submitQuoteButton}
                        onClick={submitForQuoteClickHandler}
                        data-test-selector="xngMiniCart_submitQuote"
                    >
                        {submitForQuoteLabel}
                    </Link>
                </StyledWrapper>
            )}
            <StyledWrapper {...minicartStyles.minicartCheckoutButton}>
                <Button
                    sizeVariant="medium"
                    onClick={() => {
                        closeOnClick?.(false);
                        if (cartPageUrl) {
                            history.push(cartPageUrl);
                        }
                    }}
                    variant="primary"
                    css={css`
                        ${cart?.requiresApproval &&
                        `padding-left: 2px; padding-right: 2px; letter-spacing: 0.1px; *{font-size: 14px;} `}
                    `}
                >
                    {cart?.requiresApproval && !canSubmitRequisition
                        ? translate("Checkout for Approval")
                        : isPunchOutOrder
                        ? translate("Return to Procurement System")
                        : translate("Checkout")}
                </Button>
            </StyledWrapper>
            {unAuthDisable &&
                parse(
                    translate(
                        `<p class="signInToCheckout">Please {0} or {1} to continue checkout</p>`,
                        `<a href="${signInPageUrl}">${translate("sign in")}</a>`,
                        `<a href="${signUpPageUrl}">${translate("register an account")}</a>`,
                    ),
                    parserOptions,
                )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCartActionButtons));
